'use client';

import React, { useState } from 'react';
import Link from 'next/link';

import {
  ArrowLeft, Calendar, Tag, Paperclip, MessageSquare,
  CheckSquare, Plus, Trash2, Send, Loader2, Edit2, Check, X,
  User, Flag,
} from 'lucide-react';
import { PROJECTS, TEAM_MEMBERS, type TaskStatus, type Priority } from '@/lib/mockData';
import PriorityBadge from '@/components/ui/PriorityBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

const task = PROJECTS[0].tasks[2]; // "Implement responsive navigation component"

const AVAILABLE_TAGS = ['frontend', 'backend', 'design', 'ux', 'performance', 'seo', 'testing', 'mobile', 'api', 'content'];

export default function TaskDetailContent() {
  const [title, setTitle] = useState(task.title);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [priority, setPriority] = useState<Priority>(task.priority);
  const [assigneeId, setAssigneeId] = useState(task.assigneeId);
  const [dueDate, setDueDate] = useState(task.dueDate);
  const [tags, setTags] = useState<string[]>(task.tags);
  const [subTasks, setSubTasks] = useState(task.subTasks);
  const [newSubTask, setNewSubTask] = useState('');
  const [comments, setComments] = useState(task.comments);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [saving, setSaving] = useState(false);

  const assignee = TEAM_MEMBERS.find((m) => m.id === assigneeId);
  const creator = TEAM_MEMBERS.find((m) => m.id === task.createdById);

  const handleSave = async () => {
    setSaving(true);
    // Backend integration point: PATCH /api/tasks/:id
    await new Promise((r) => setTimeout(r, 700));
    setSaving(false);
    toast.success('Task saved successfully');
  };

  const handleTitleSave = () => {
    setTitle(titleDraft);
    setEditingTitle(false);
  };

  const toggleSubTask = (id: string) => {
    setSubTasks((prev) =>
      prev.map((st) => (st.id === id ? { ...st, completed: !st.completed } : st))
    );
  };

  const addSubTask = () => {
    if (!newSubTask.trim()) return;
    setSubTasks((prev) => [
      ...prev,
      { id: `st-new-${Date.now()}`, title: newSubTask.trim(), completed: false },
    ]);
    setNewSubTask('');
  };

  const deleteSubTask = (id: string) => {
    setSubTasks((prev) => prev.filter((st) => st.id !== id));
  };

  const sendComment = async () => {
    if (!newComment.trim()) return;
    setSendingComment(true);
    // Backend integration point: POST /api/tasks/:id/comments
    await new Promise((r) => setTimeout(r, 400));
    setComments((prev) => [
      ...prev,
      {
        id: `c-new-${Date.now()}`,
        authorId: 'user-001',
        authorName: 'Alex Morgan',
        authorInitials: 'AM',
        authorColor: '#7c3aed',
        content: newComment.trim(),
        createdAt: new Date().toISOString(),
      },
    ]);
    setNewComment('');
    setSendingComment(false);
  };

  const toggleTag = (tag: string) => {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const completedSubTasks = subTasks.filter((s) => s.completed).length;
  const subTaskProgress = subTasks.length > 0
    ? Math.round((completedSubTasks / subTasks.length) * 100)
    : 0;

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 fade-in">
      {/* Back nav */}
      <Link
        href="/project-detail"
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors mb-5"
      >
        <ArrowLeft size={13} /> Back to Website Redesign
      </Link>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main content — left 2/3 */}
        <div className="xl:col-span-2 space-y-5">
          {/* Title */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-start gap-3 mb-4">
              {editingTitle ? (
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="text"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    className="flex-1 text-xl font-semibold text-foreground bg-muted border border-primary/30 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-primary/30"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleTitleSave();
                      if (e.key === 'Escape') setEditingTitle(false);
                    }}
                  />
                  <button onClick={handleTitleSave} className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                    <Check size={16} />
                  </button>
                  <button onClick={() => setEditingTitle(false)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                    <X size={16} className="text-muted-foreground" />
                  </button>
                </div>
              ) : (
                <div className="flex-1 flex items-start gap-2 group">
                  <h1 className="text-xl font-semibold text-foreground flex-1 leading-snug">{title}</h1>
                  <button
                    onClick={() => { setTitleDraft(title); setEditingTitle(true); }}
                    className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-muted transition-all text-muted-foreground"
                    title="Edit title"
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Status + Priority row */}
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={status} />
              <PriorityBadge priority={priority} />
              <span className="text-xs text-muted-foreground">
                in <span className="font-medium text-foreground">Website Redesign</span>
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="bg-card rounded-xl border border-border p-5">
            <h3 className="text-sm font-semibold text-foreground mb-3">Description</h3>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Add a description for this task..."
              className="w-full text-sm text-foreground bg-muted/50 border border-transparent rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30 focus:bg-background transition-all resize-none placeholder:text-muted-foreground"
            />
          </div>

          {/* Sub-tasks */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckSquare size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Sub-tasks</h3>
                <span className="text-xs text-muted-foreground font-medium">
                  {completedSubTasks}/{subTasks.length} completed
                </span>
              </div>
              <span className="text-xs font-semibold text-muted-foreground font-tabular">
                {subTaskProgress}%
              </span>
            </div>

            {/* Progress bar */}
            {subTasks.length > 0 && (
              <div className="h-1.5 bg-muted rounded-full mb-4 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full progress-bar-fill"
                  style={{ width: `${subTaskProgress}%` }}
                />
              </div>
            )}

            <div className="space-y-2 mb-3">
              {subTasks.map((st) => (
                <div
                  key={`subtask-${st.id}`}
                  className="flex items-center gap-3 group py-1.5 px-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <button
                    onClick={() => toggleSubTask(st.id)}
                    className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                      st.completed
                        ? 'bg-primary border-primary' :'border-border hover:border-primary/50'
                    }`}
                    style={{ width: '18px', height: '18px' }}
                  >
                    {st.completed && <Check size={10} className="text-white" />}
                  </button>
                  <span
                    className={`text-sm flex-1 ${
                      st.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                    }`}
                  >
                    {st.title}
                  </span>
                  <button
                    onClick={() => deleteSubTask(st.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-all"
                    title="Delete sub-task — this cannot be undone"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>

            {/* Add sub-task */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newSubTask}
                onChange={(e) => setNewSubTask(e.target.value)}
                placeholder="Add a sub-task..."
                onKeyDown={(e) => e.key === 'Enter' && addSubTask()}
                className="flex-1 text-sm px-3 py-2 border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground"
              />
              <button
                onClick={addSubTask}
                disabled={!newSubTask.trim()}
                className="btn-primary p-2 rounded-lg disabled:opacity-40"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* Comments */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">
                Comments <span className="text-muted-foreground font-normal">({comments.length})</span>
              </h3>
            </div>

            <div className="space-y-4 mb-4">
              {comments.map((comment) => (
                <div key={`comment-${comment.id}`} className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: comment.authorColor }}
                  >
                    {comment.authorInitials}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-foreground">{comment.authorName}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(comment.createdAt).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div className="bg-muted rounded-lg px-3 py-2.5">
                      <p className="text-sm text-foreground">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}

              {comments.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No comments yet. Be the first to comment.
                </p>
              )}
            </div>

            {/* Add comment */}
            <div className="flex items-start gap-3">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                style={{ backgroundColor: '#7c3aed' }}
              >
                AM
              </div>
              <div className="flex-1 relative">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={2}
                  className="w-full text-sm px-3 py-2.5 pr-10 border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none placeholder:text-muted-foreground"
                />
                <button
                  onClick={sendComment}
                  disabled={!newComment.trim() || sendingComment}
                  className="absolute bottom-2.5 right-2.5 p-1.5 rounded-lg bg-primary text-white disabled:opacity-40 hover:bg-primary/90 transition-colors"
                >
                  {sendingComment ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Send size={13} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-2 mb-4">
              <Paperclip size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">
                Attachments <span className="text-muted-foreground font-normal">({task.attachments.length})</span>
              </h3>
            </div>

            <div className="space-y-2 mb-3">
              {task.attachments.map((att) => (
                <div
                  key={`att-${att.id}`}
                  className="flex items-center gap-3 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Paperclip size={14} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{att.name}</p>
                    <p className="text-xs text-muted-foreground">{att.size} · Uploaded {att.uploadedAt}</p>
                  </div>
                </div>
              ))}

              {task.attachments.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-3">No attachments yet.</p>
              )}
            </div>

            <button className="flex items-center gap-2 text-sm text-primary font-medium hover:underline">
              <Plus size={14} />
              Attach file
            </button>
          </div>
        </div>

        {/* Sidebar — right 1/3 */}
        <div className="xl:col-span-1 space-y-4">
          {/* Properties */}
          <div className="bg-card rounded-xl border border-border p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Properties</h3>

            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TaskStatus)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {(['To Do', 'In Progress', 'In Review', 'Done'] as TaskStatus[]).map((s) => (
                    <option key={`status-opt-${s}`} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Priority
                </label>
                <div className="flex gap-2">
                  {(['High', 'Medium', 'Low'] as Priority[]).map((p) => (
                    <button
                      key={`prio-${p}`}
                      onClick={() => setPriority(p)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                        priority === p
                          ? p === 'High' ?'bg-red-100 text-red-600 ring-2 ring-red-200'
                            : p === 'Medium' ?'bg-orange-100 text-orange-600 ring-2 ring-orange-200' :'bg-green-100 text-green-600 ring-2 ring-green-200' :'bg-muted text-muted-foreground hover:bg-border'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assignee */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Assignee
                </label>
                <div className="space-y-1.5">
                  {TEAM_MEMBERS.map((m) => (
                    <button key={`assignee-opt-${m.id}`}
                      onClick={() => setAssigneeId(m.id)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-150 text-left ${
                        assigneeId === m.id
                          ? 'bg-primary/10 ring-1 ring-primary/30' :'hover:bg-muted/50'
                      }`}
                    >
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                        style={{ backgroundColor: m.avatarColor }}
                      >
                        {m.avatarInitials}
                      </div>
                      <span className="text-sm text-foreground">{m.name}</span>
                      {assigneeId === m.id && (
                        <Check size={13} className="ml-auto text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Due Date
                </label>
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center gap-2 mb-3">
              <Tag size={14} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Tags</h3>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_TAGS.map((tag) => (
                <button
                  key={`tag-toggle-${tag}`}
                  onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                    tags.includes(tag)
                      ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-border'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-card rounded-xl border border-border p-5">
            <h3 className="text-sm font-semibold text-foreground mb-3">Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User size={13} className="text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground">Created by</span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                    style={{ backgroundColor: creator?.avatarColor }}
                  >
                    {creator?.avatarInitials}
                  </div>
                  <span className="text-xs font-medium text-foreground">{creator?.name}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={13} className="text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground">Created on</span>
                <span className="text-xs font-medium text-foreground ml-auto">
                  May 1, 2026
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Flag size={13} className="text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground">Project</span>
                <Link
                  href="/project-detail"
                  className="text-xs font-medium text-primary hover:underline ml-auto"
                >
                  Website Redesign
                </Link>
              </div>
            </div>
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
          >
            {saving ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}